import {Box, Typography, useTheme} from '@mui/material'
import type {Theme} from '@mui/material'
import type {FC, ReactElement} from 'react'
import {memo} from 'react'
import {useUserContext} from '@containers/App/UserProvider'
import type {UserContextProps} from '@containers/App/UserProvider'
import type {UserInterface} from '@interfaces/user'
import {chartPanelSx} from './chartStyles'
import type {StatItem, StatTileSpec} from './types'

/**
 * KPI / stat tile(s). One big value with optional unit, target, and signed delta —
 * or a row of such tiles via `items`. Pure MUI, no d3. `status` (good/warn/bad)
 * tints the value + delta; otherwise they stay neutral (delta direction only).
 */

interface Props {
  spec: StatTileSpec
}

const statusColor = (theme: Theme, status?: StatItem['status']): string | undefined => {
  if (status === 'good') return theme.palette.success.main
  if (status === 'warn') return theme.palette.warning.main
  if (status === 'bad') return theme.palette.error.main
  return undefined
}

const fmt = (v: number | string, locale?: string): string =>
  typeof v === 'number' ? new Intl.NumberFormat(locale, {maximumFractionDigits: 2}).format(v) : v

const Tile: FC<{item: StatItem; locale?: string}> = ({item, locale}) => {
  const theme = useTheme()
  const color = statusColor(theme, item.status)
  const hasDelta = typeof item.delta === 'number' && item.delta !== 0

  return (
    <Box sx={{minWidth: 110, flex: '0 1 auto'}}>
      {item.label ? (
        <Typography variant="caption" sx={{color: 'text.secondary', display: 'block'}}>
          {item.label}
        </Typography>
      ) : null}
      <Box sx={{display: 'flex', alignItems: 'baseline', gap: 0.75, mt: 0.25}}>
        <Typography
          variant="h5"
          sx={{fontWeight: 700, lineHeight: 1, color: color ?? 'text.primary'}}
        >
          {fmt(item.value, locale)}
          {item.unit ? (
            <Typography component="span" variant="body2" sx={{color: 'text.secondary', ml: 0.25}}>
              {item.unit}
            </Typography>
          ) : null}
        </Typography>
        {hasDelta ? (
          <Typography variant="caption" sx={{fontWeight: 600, color: color ?? 'text.secondary'}}>
            {(item.delta as number) > 0 ? '▲' : '▼'} {fmt(Math.abs(item.delta as number), locale)}
          </Typography>
        ) : null}
      </Box>
      {item.target != null || item.deltaLabel ? (
        <Typography variant="caption" sx={{color: 'text.secondary', display: 'block', mt: 0.25}}>
          {item.target != null ? `/ ${fmt(item.target, locale)}${item.unit ?? ''}` : ''}
          {item.target != null && item.deltaLabel ? ' · ' : ''}
          {item.deltaLabel ?? ''}
        </Typography>
      ) : null}
    </Box>
  )
}

const ChatStatTile: FC<Props> = ({spec}): ReactElement | null => {
  // Format numbers in the user's locale (consistent with the localized line axis).
  const {currentUser} = (useUserContext() as UserContextProps | null) ?? {currentUser: null}
  const profile = (currentUser as UserInterface | null)?.userprofile
  const locale = profile?.preferred_locale || profile?.preferred_language || undefined

  const items: StatItem[] = spec.items?.length
    ? spec.items
    : spec.value != null
      ? [
          {
            label: spec.label ?? spec.title ?? '',
            value: spec.value,
            unit: spec.unit,
            target: spec.target,
            delta: spec.delta,
            deltaLabel: spec.deltaLabel,
            status: spec.status,
          },
        ]
      : []

  if (!items.length) return null
  const showTitle = Boolean(spec.title && spec.items?.length)

  return (
    <Box sx={chartPanelSx}>
      {showTitle ? (
        <Typography variant="subtitle2" sx={{mb: 1, fontWeight: 600}}>
          {spec.title}
        </Typography>
      ) : null}
      <Box sx={{display: 'flex', flexWrap: 'wrap', columnGap: 3, rowGap: 1.5}}>
        {items.map((it, i) => (
          <Tile key={`${it.label}-${i}`} item={it} locale={locale} />
        ))}
      </Box>
    </Box>
  )
}

export default memo(ChatStatTile)
